import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { MODULES, setSelectedModule } from "../appModule/appModuleSlice";
import { logout } from "../authentication/authSlice";

// Resolve module-scoped flags from the active app module, so tower create/read
// carry isLab / isClassAdmin / isBmb without every caller passing them. Explicit
// flags on the action payload win (some callers already send them).
const resolveModuleFlags = (thunkAPI, obj = {}) => {
  const selectedModule = thunkAPI.getState()?.appModule?.selectedModule;
  return {
    isClassroom:
      obj.isClassroom ?? obj.isClassroom ?? selectedModule === MODULES.EDUSPACE,
    isLab: obj.isLab ?? selectedModule === MODULES.MAKERSLAB,
    isBmb: obj.isBmb ?? selectedModule === MODULES.BLOOMBERG,
  };
};

let userId;
let token;
let headers;
let API = process.env.REACT_APP_API;
let authData = Cookies.get("loginData")
? JSON.parse(Cookies.get("loginData"))
: null;
let rolesData = Cookies.get("roles")
? JSON.parse(Cookies.get("roles"))
: null;
let organizationId = (rolesData?.includes('Cowork Lab Admin') || rolesData?.includes('Cowork Lab Student')) ? '52c3b93c-59ba-44e7-8e99-bd5fc889b68f' : authData?.organizationId;

const setTokenValues = () => {
  const data = Cookies.get("loginData")
    ? JSON.parse(Cookies.get("loginData"))
    : null;
  userId = data?.id;
  token = Cookies.get("token");
  headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

// Admin Routes
// Create New Room
export const postRoom = createAsyncThunk(
  "cowork/postRoom",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(`${API}/api/cowork/rooms/addRoom`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          room_name: obj.roomName,
          capacity: obj.roomCapacity,
          tower: obj.tower,
          availability: obj.availability,
          room_thumbnail: obj.room_thumbnail,
          roomImages: obj.roomImages || [],
          availableDays: obj.availableDays || [],
          availableFrom: obj.availableFrom || null,
          availableTo: obj.availableTo || null,
          isClassAdmin : obj.isClassAdmin,
          isLab : obj.isLab,
          isBmb : obj.isBmb,
          category: obj.category,
          organizationId : obj.organizationId,
          equipments : obj.equipments || [],
          amenities : obj.amenities || []
        }),
      });
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const postTower = createAsyncThunk(
  "cowork/add-towerAndAmenities",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      // Tag a new tower with the active module (amenities stay global).
      const moduleFlags = resolveModuleFlags(thunkAPI, obj);
      const response = await fetch(`${API}/api/cowork/roomBookingAdmin/add-towerAndAmenities`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          towerName : obj.towerName || '',
          amenities : obj.amenities || '',
          ...(obj.towerName ? moduleFlags : {}),
        }),
      });
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const getEquipmentList = createAsyncThunk(
  "cowork/getEquipmentList",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(`${API}/api/cowork/roomBookingAdmin/getAllEquipments`, {
        method: "GET",
        headers,
      });
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const getAllTowers = createAsyncThunk(
  "cowork/getAllTowers",
  async (obj = {}, thunkAPI) => {
    try {
      setTokenValues();
      // Scope to the active module (CoWork → all towers). Explicit flags win.
      const { isClassroom, isLab, isBmb } = resolveModuleFlags(thunkAPI, obj);
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/getAllTowers?isClassAdmin=${isClassroom}&isLab=${isLab}&isBmb=${isBmb}`,
        {
          method: "GET",
          headers,
          // Aborting the dispatched thunk (useLatestRequest) has to abort the
          // HTTP request as well — otherwise the superseded duplicate still
          // runs to completion on the server.
          signal: thunkAPI.signal,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      // Let RTK turn a deliberate abort into a `meta.aborted` rejection.
      if (error.name === "AbortError") throw error;
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);
export const getAllAmenities = createAsyncThunk(
  "cowork/getAllAmenities",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(`${API}/api/cowork/roomBookingAdmin/getAllAmenities`, {
        method: "GET",
        headers,
      });
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const postEquipment = createAsyncThunk(
  "cowork/add-equipment",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(`${API}/api/cowork/roomBookingAdmin/add-equipment`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          equipmentName : obj.equipmentName,
          category : obj.category,
        }),
      });
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// ---- Booking color types (admin color coding for the Eduspace calendar) ----
export const getBookingColorTypes = createAsyncThunk(
  "cowork/getBookingColorTypes",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(`${API}/api/cowork/roomBookingAdmin/getAllBookingColorTypes`, {
        method: "GET",
        headers,
      });
      let data = await response.json();
      if (response.status === 200) return data;
      return thunkAPI.rejectWithValue(data);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const addBookingColorType = createAsyncThunk(
  "cowork/addBookingColorType",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(`${API}/api/cowork/roomBookingAdmin/add-bookingColorType`, {
        method: "POST",
        headers,
        // school_id is set only for the rows that hold a school's colour (see
        // MakersV2/Constants/schools.js). Harmless while the column doesn't
        // exist — the name carries the same info — and picked up automatically
        // once it does.
        body: JSON.stringify({ name: obj.name, color: obj.color, school_id: obj.school_id || null }),
      });
      let data = await response.json();
      if (response.status === 200) return data;
      return thunkAPI.rejectWithValue(data);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const updateBookingColorType = createAsyncThunk(
  "cowork/updateBookingColorType",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(`${API}/api/cowork/roomBookingAdmin/update-bookingColorType/${obj.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ name: obj.name, color: obj.color, school_id: obj.school_id || null }),
      });
      let data = await response.json();
      if (response.status === 200) return data;
      return thunkAPI.rejectWithValue(data);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const deleteBookingColorType = createAsyncThunk(
  "cowork/deleteBookingColorType",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(`${API}/api/cowork/roomBookingAdmin/delete-bookingColorType/${obj.id}`, {
        method: "DELETE",
        headers,
      });
      let data = await response.json();
      if (response.status === 200) return data;
      return thunkAPI.rejectWithValue(data);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Assign (or clear with color_type_id null) a request's color category.
export const setRequestColorType = createAsyncThunk(
  "cowork/setRequestColorType",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(`${API}/api/cowork/roomBookingAdmin/request/${obj.requestId}/color-type`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ color_type_id: obj.color_type_id || null }),
      });
      let data = await response.json();
      if (response.status === 200) return data;
      return thunkAPI.rejectWithValue(data);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Get All Bookings
export const getAllBookings = createAsyncThunk(
  "cowork/getAllBookings",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBooking/allbookings/${obj.pageNumber}/${obj.perPageCount}?searchQuery=${obj.searchValue || ''}&room=${obj.filters.room || ''}&tower=${obj.filters.tower}&meetingDate=${obj.filters.meetingDate || ''}&status=${obj.filters.status}&role=${obj.filters.role || ''}&isClassroom=${obj.isClassroom || ''}&isLab=${obj.isLab || ''}&isBmb=${obj.isBmb || ''}`,
        {
          method: "GET",
          headers,
          signal: thunkAPI.signal,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      if (error.name === "AbortError") throw error;
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Get All Rooms
export const getAllRooms = createAsyncThunk(
  "cowork/getAllRooms",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/rooms/allrooms/${obj.pageNumber}/${obj.perPageCount}?organizationId=${obj?.organizationId}&searchQuery=${obj.searchValue}&tower=${obj.filters.tower}&capacity=${obj.filters.capacity}&availability=${obj.filters.availability}&isClassAdmin=${obj.isClassAdmin}&isLab=${obj.isLab || ''}&isBmb=${obj.isBmb || ''}`,
        {
          method: "GET",
          headers,
          signal: thunkAPI.signal,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      if (error.name === "AbortError") throw error;
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Update Room Details
export const postUpdateRoom = createAsyncThunk(
  "cowork/postUpdateRoom",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/rooms/updateRoom/${obj.id}`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({
            room_name: obj.roomName,
            capacity: obj.roomCapacity,
            tower: obj.tower,
            tower_id: obj.tower_id,
            availability: obj.availability,
            room_thumbnail: obj.room_thumbnail,
            roomImages: obj.roomImages || [],
            availableDays: obj.availableDays || [],
            availableFrom: obj.availableFrom || null,
            availableTo: obj.availableTo || null,
            category: obj.category,
            equipments : obj.equipments || [],
            amenities : obj.amenities || []
          }),
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const postRoomActivity = createAsyncThunk(
  "cowork/postRoomActivity",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/rooms/updateAvailability/${userId}/${obj.id}`,
        {
          method: "PUT",
          headers,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// USER ROUTES

/**GET ALL Available Rooms  */
export const getAvailableRooms = createAsyncThunk(
  "cowork/getAvailableRooms",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/rooms/fetchAvailableAllRoomsandSlots/${userId}/${obj.pageNumber}/${obj.perPageCount}?meetingDate=${obj.meetingDate}&searchQuery=${obj.searchValue}&tower=${obj.filters.tower}&capacity=${obj.filters.capacity}&isStudent=${obj.isStudent}&isAdmin=${obj.isAdmin}&isClassAdmin=${obj.isClassAdmin}&isBmb=${obj.isBmb || ''}`,
        {
          method: "GET",
          headers,
          signal: thunkAPI.signal,
        }
      );

      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      // Let RTK turn a deliberate abort into a `meta.aborted` rejection.
      if (error.name === "AbortError") throw error;
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Add A new Booking
export const postRoomBookings = createAsyncThunk(
  "cowork/postRoomBookings",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBooking/bookroom/${userId}/${obj.room_id}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            date: obj.meetingDate,
            slot: obj.meetingSlot,
            meeting_title: obj.meetingDetail,
            attendees: obj.selectedAttendees,
          }),
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const getMyBookings = createAsyncThunk(
  "cowork/getMyBookings",
  async (obj, thunkAPI) => {
    console.log(obj);
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBooking/mybookings/${userId}/${obj.pageNumber}/${obj.perPageCount}?searchQuery=${obj.searchValue}&tower=${obj.filters.tower}&status=${obj.filters.status}&eventStatus=${obj.status}`,
        {
          method: "GET",
          headers,
          signal: thunkAPI.signal,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      if (error.name === "AbortError") throw error;
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const cancelABooking = createAsyncThunk(
  "cowork/cancelABooking",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      console.log(obj)
      const response = await fetch(
        `${API}/api/cowork/roomBooking/cancelBooking/${obj.id}/${userId}`,
        {
          method: "PUT",
          headers
        }
      );

      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const getAvailableSlots = createAsyncThunk(
  "cowork/getAvailableSlots",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBooking/fetchSlots/${obj.meetingDate}/${obj.id}/${userId}`,
        {
          method: "GET",
          headers,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const getAllCoworkUsers = createAsyncThunk(
  "cowork/getAllCoworkUsers",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBooking/allcoworkusers/${userId}?isLabStudent=${obj.isLabStudent}&isBmbStudent=${obj.isBmbStudent || ''}`,
        {
          method: "GET",
          headers,
          signal: thunkAPI.signal,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      if (error.name === "AbortError") throw error;
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);



export const getBookingAtSameTime = createAsyncThunk(
  "cowork/getBookingAtSameTime",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBooking/findBookingAtSameTime/${obj.roomId}`,
        {
          method: "GET",
          headers,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const findBookingAtSameTimeDownHierarchy = createAsyncThunk(
  "cowork/findBookingAtSameTimeDownHierarchy",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBooking/findBookingAtSameTime/${obj.roomId}/${obj.meetingDate}/${obj.meetingSlot}`,
        {
          method: "GET",
          headers,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const findSlotsForOneRoom = createAsyncThunk(
  "cowork/findSlotsForOneRoom",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/rooms/fetchOneRoomSlots/${userId}/${obj.roomId}?meetingDate=${obj.meetingDate}`,
        {
          method: "GET",
          headers,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const getAdditionalCoworkUsers = createAsyncThunk(
  "cowork/getAdditionalCoworkUsers",
  async (obj, thunkAPI) => {
    try {
      console.log(obj);
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBooking/findNotIncludedAttendees/${userId}/${obj.roomBookingId}`,
        {
          method: "GET",
          headers,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const addAdditionalCoworkUsers = createAsyncThunk(
  "cowork/addAdditionalCoworkUsers",
  async (obj, thunkAPI) => {
    try {
      console.log(obj);
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBooking/addAttendees/${obj.roomBookingId}`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({
            attendees: obj.selectedAttendees,
          }),
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        console.log(data);
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);
export const postResetPasswordEmail = createAsyncThunk(
  "cowork/postResetPasswordEmail",
  async (obj, thunkAPI) => {
    try {
      // console.log(obj)
      setTokenValues();
      const response = await fetch(`${API}/api/cowork/auth/resetpasswordurl`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          email:obj.email,
          baseUrl:obj.baseUrl
        }),
      });
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);
export const postRoomBookingAdmin = createAsyncThunk(
  "cowork/postRoomBookingAdmin",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBooking/adminbookroom/${userId}/${obj.room_id}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            date: obj.meetingDate,
            slot: obj.meetingSlot,
            meeting_title: obj.meetingDetail,
            attendees: obj.selectedAttendees,
          }),
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

const initialStateValues = {
  attendeesAddedSuccessfully:false,
  isCoworkSliceFetching: false,
  isCoworkSliceSmallFetching: false,
  // My Bookings needs a flag of its own — `isCoworkSliceFetching` is shared by
  // every thunk here, so the tab couldn't tell whether ITS list was loading.
  isMyBookingsFetching: false,
  isCoworkSliceFetchingRoomBooking: false,
  isCoworkSliceSuccess: false,
  isCoworkSliceError: false,
  isCoworkSliceUserFetch:false,
  isgettingAllbookings : false,
  isgettingEquipmentList:false,
  isgettingEquipmentListSuccess:false,
  isgettingEquipmentListError:false,
  isgettingTowerList:false,
  isgettingTowerListSuccess:false,
  isgettingTowerListError:false,
  coworkSliceSuccessMessage: "",
  coworkSliceErrorMessage: "",
  roomSelectionDetail: null,
  availableRoomsList: [],
  newlyAddedRoomsList : [],
  towersList : [],
  amenitiesList : [],
  equipmentList : [],
  availableRoomsCount: 0,
  myBookingsList: [],
  myBookingCount: 0,
  bookingCancelledSuccesfully: false,
  bookedSlots: [],
  coworkUsers: [],
  bookingAtSameTimeMessage: "",
  BookingAtSameTimeDownHierarchyMessage: "",
  AllBookingList: [],
  AllBookingCount: 0,
  AllRooms: [],
  AllRoomsCount: 0,
  roomToBeUpdatedDetails: null,
  selectedDate: null,
  roomAlreadyBooked:false,
  selectedSlot: null,
  selectedAttendees:[],
  slotsForOneRoom:[],
  additionalUsers:[],
  selectedAdditionalAttendees:[],
  finalMultipleSlotsForAdmin:[],
  availableRoomMyBookings:[],
  bookingColorTypes: [],
  isColorTypeMutating: false,
  colorTypeMutationSuccess: false,
};

const coworkSlice = createSlice({
  name: "cowork",
  initialState: initialStateValues,
  reducers: {
    // Reducer Action to clear initial states after success from any AsynCThunk call
    clearCoworkSliceStates: (state, action) => {
      state.isCoworkSliceFetching = false;
        state.isCoworkSliceSmallFetching = false;
        state.isMyBookingsFetching = false;
        state.isCoworkSliceSuccess = false;
        state.isCoworkSliceError = false;
        state.coworkSliceSuccessMessage = "";
        state.coworkSliceErrorMessage = "";
        state.bookingCancelledSuccesfully = false;
      return state;
    },
    clearCoworkBookingsList: (state, action) => {
      state.AllBookingList = [];
      state.AllBookingCount = 0;
      return state;
    },
    clearCoworkSliceData: (state, action) => {
      state.availableRoomsList = [];
      state.newlyAddedRoomsList = [];
      state.myBookingsList = [];
      state.AllBookingList = [];
      state.slotsForOneRoom = [];
      state.AllRooms = [];
      state.selectedAttendees = [];
      state.availableRoomsCount = 0;
      state.myBookingCount = 0;
      state.AllBookingCount = 0;
      state.AllRoomsCount = 0;
      return state;
    },
    clearRoomDetail: (state, action) => {
      state.roomSelectionDetail = null;
      return state;
    },
    addRoomDetails: (state, action) => {
      state.roomSelectionDetail = action.payload;
      return state;
    },
    addRoomDetailsToBeUpdated: (state, action) => {
      state.roomToBeUpdatedDetails = action.payload;
      return state;
    },
    addSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
      return state;
    },

    addSelectedSlot: (state, action) => {
      state.selectedSlot = action.payload;
      return state;
    },
    addRoomStatus: (state, action) => {
      state.roomAlreadyBooked = action.payload;
      return state;
    },
    clearSelectedSlot: (state, action) => {
      state.selectedSlot = null;
      return state;
    },
    addSelectedAttendees: (state, action) => {
      state.selectedAttendees = action.payload;
      return state;
    },
    clearSelectedAttendees: (state, action) => {
      state.selectedAttendees = [];
      return state;
    },
    addAdditionSelectedAttendees: (state, action) => {
      console.log(action.payload);
      state.selectedAdditionalAttendees = action.payload;
      return state;
    },
    clearAdditionalSelectedAttendees: (state, action) => {
      state.selectedAdditionalAttendees = [];
      return state;
    },
    addFinalMultipleSlotsForAdmin: (state, action) => {
      state.finalMultipleSlotsForAdmin = action.payload;
      return state;
    },
    clearFinalMultipleSlotsForAdmin: (state, action) => {
      state.finalMultipleSlotsForAdmin = [];
      return state;
    },
  },
  extraReducers: (builder) => {
    // Wipe everything on logout so the next user never sees the previous one's
    // rooms/bookings.
    builder.addCase(logout, () => initialStateValues);
    // Switching modules (CoWork ↔ Makers Lab ↔ Eduspace ↔ Bloomberg) must not
    // blend one module's rooms/towers/bookings into another — drop the loaded
    // lists so each module re-fetches its own from a clean slate.
    builder.addCase(setSelectedModule, (state) => {
      state.availableRoomsList = [];
      state.newlyAddedRoomsList = [];
      state.myBookingsList = [];
      state.AllBookingList = [];
      state.AllRooms = [];
      state.slotsForOneRoom = [];
      state.towersList = [];
      state.bookedSlots = [];
      state.roomSelectionDetail = null;
      state.roomToBeUpdatedDetails = null;
      state.availableRoomsCount = 0;
      state.myBookingCount = 0;
      state.AllBookingCount = 0;
      state.AllRoomsCount = 0;
    });
    // CASES FOR GET COHORT FEE
    builder.addCase(getAvailableRooms.fulfilled, (state, { payload, meta }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceSmallFetching = false;
      const incomingRooms = payload?.roomAndSlots || [];
      const incomingTowers = payload?.towers || [];
      // Page 1 replaces so a room from a previous module/session can't linger at
      // the top; "load more" (page > 1) appends.
      const isFirstPage = (meta?.arg?.pageNumber ?? 1) <= 1;
      state.availableRoomsList = isFirstPage
        ? incomingRooms
        : [...state.availableRoomsList, ...incomingRooms];
      state.towersList = isFirstPage
        ? incomingTowers
        : [...state.towersList, ...incomingTowers];
      state.availableRoomMyBookings = payload?.myBookings;
      state.availableRoomsCount = payload.roomAndSlotsCount;
      return state;
    });
    builder.addCase(getAvailableRooms.rejected, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceSmallFetching = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getAvailableRooms.pending, (state, { payload }) => {
      state.isCoworkSliceFetching = true;
      state.isCoworkSliceSmallFetching = true;
      return state;
    });
    builder.addCase(postRoomBookings.fulfilled, (state, { payload }) => {
      state.isCoworkSliceFetchingRoomBooking = false;
      state.isCoworkSliceSuccess = true;
      state.coworkSliceSuccessMessage = "Room Booked Successfully";
      return state;
    });
    builder.addCase(postRoomBookings.rejected, (state, { payload }) => {
      state.isCoworkSliceFetchingRoomBooking = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(postRoomBookings.pending, (state, { payload }) => {
      state.isCoworkSliceFetchingRoomBooking = true;
      return state;
    });
    builder.addCase(getMyBookings.fulfilled, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceSmallFetching = false;
      state.isMyBookingsFetching = false;
      state.myBookingsList = [...state.myBookingsList, ...payload.bookings];
      state.myBookingCount = payload.myBookingsCount;
      return state;
    });
    builder.addCase(getMyBookings.rejected, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceSmallFetching = false;
      state.isMyBookingsFetching = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getMyBookings.pending, (state, { payload }) => {
      state.isCoworkSliceFetching = true;
      state.isCoworkSliceSmallFetching = true;
      state.isMyBookingsFetching = true;
      return state;
    });
    builder.addCase(cancelABooking.fulfilled, (state, { payload }) => {
      state.isCoworkSliceSmallFetching = false;
      state.isCoworkSliceSuccess = true;
      console.log(payload?.updatedRoom);
      let updatedMyBookingIndex = state.myBookingsList.findIndex(
        (data) => data.id === payload?.updatedRoom.id
      );
      let updatedAllBookingIndex = state.AllBookingList.findIndex(
        (data) => data.id === payload?.updatedRoom.id
      );

      console.log(updatedMyBookingIndex, updatedAllBookingIndex);
      if(updatedMyBookingIndex != -1){ 
        state.myBookingsList[updatedMyBookingIndex] = payload?.updatedRoom;
      }else if(updatedAllBookingIndex != -1){ 
        state.AllBookingList[updatedAllBookingIndex] = payload?.updatedRoom;
      }

      state.coworkSliceSuccessMessage = "Booking Cancelled Successfully";
      return state;
    });
    builder.addCase(cancelABooking.rejected, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(cancelABooking.pending, (state, { payload }) => {
      state.isCoworkSliceSmallFetching = true;
      return state;
    });
    builder.addCase(getAvailableSlots.fulfilled, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.bookingCancelledSuccesfully = true;
      state.bookedSlots = payload?.fetchedSlotsBooked || [];
      return state;
    });
    builder.addCase(getAvailableSlots.rejected, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getAvailableSlots.pending, (state, { payload }) => {
      state.isCoworkSliceFetching = true;
      return state;
    });
    builder.addCase(getAllCoworkUsers.fulfilled, (state, { payload }) => {
      state.isCoworkSliceUserFetch = false;
      state.coworkUsers = payload?.users || [];
      return state;
    });
    builder.addCase(getAllCoworkUsers.rejected, (state, { payload }) => {
      state.isCoworkSliceUserFetch = false;
      // state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getAllCoworkUsers.pending, (state, { payload }) => {
      state.isCoworkSliceUserFetch = true;
      return state;
    });
    builder.addCase(
      findBookingAtSameTimeDownHierarchy.fulfilled,
      (state, { payload }) => {
        state.isCoworkSliceSmallFetching = false;
        state.BookingAtSameTimeDownHierarchyMessage =
          payload?.message || "Are You Sure, You want to book the Room";
        return state;
      }
    );
    builder.addCase(
      findBookingAtSameTimeDownHierarchy.rejected,
      (state, { payload }) => {
        state.isCoworkSliceSmallFetching = false;
        state.isCoworkSliceError = true;
        state.coworkSliceErrorMessage =
          payload?.message || "Something Went Wrong";
        return state;
      }
    );
    builder.addCase(
      findBookingAtSameTimeDownHierarchy.pending,
      (state, { payload }) => {
        state.isCoworkSliceSmallFetching = true;
        return state;
      }
    );
    builder.addCase(postRoom.fulfilled, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceSuccess = true;
      state.coworkSliceSuccessMessage = payload?.message || " ";
      return state;
    });
    builder.addCase(postRoom.rejected, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(postRoom.pending, (state, { payload }) => {
      state.isCoworkSliceFetching = true;
      return state;
    });

    builder.addCase(getEquipmentList.fulfilled, (state, { payload }) => {
      state.isgettingEquipmentList = false;
      state.isgettingEquipmentListSuccess = true;
      state.coworkSliceSuccessMessage = payload?.message || " ";
      state.equipmentList = payload?.equipments;
      return state;
    });
    builder.addCase(getEquipmentList.rejected, (state, { payload }) => {
      state.isgettingEquipmentList = false;
      state.isgettingEquipmentListError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getEquipmentList.pending, (state, { payload }) => {
      state.isgettingEquipmentList = true;
      return state;
    });

    builder.addCase(getAllTowers.fulfilled, (state, { payload }) => {
      state.isgettingTowerList = false;
      state.isgettingTowerListSuccess = true;
      state.coworkSliceSuccessMessage = payload?.message || " ";
      state.towersList = payload?.towers;
      return state;
    });
    builder.addCase(getAllTowers.rejected, (state, { payload }) => {
      state.isgettingTowerList = false;
      state.isgettingTowerListError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getAllTowers.pending, (state, { payload }) => {
      state.isgettingTowerList = true;
      return state;
    });
    builder.addCase(getAllAmenities.fulfilled, (state, { payload }) => {
      state.isgettingTowerList = false;
      state.isgettingTowerListSuccess = true;
      state.coworkSliceSuccessMessage = payload?.message || " ";
      state.amenitiesList = payload?.amenities;
      return state;
    });
    builder.addCase(getAllAmenities.rejected, (state, { payload }) => {
      state.isgettingTowerList = false;
      state.isgettingTowerListError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getAllAmenities.pending, (state, { payload }) => {
      state.isgettingTowerList = true;
      return state;
    });

    builder.addCase(getAllBookings.fulfilled, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceSmallFetching = false;
      state.isgettingAllbookings = false;
      state.coworkSliceSuccessMessage = payload?.message || " ";
      state.AllBookingList = [...state.AllBookingList, ...payload?.bookings];
      state.AllBookingCount = payload?.allBookingsCount;
      return state;
    });
    builder.addCase(getAllBookings.rejected, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceSmallFetching = false;
      state.isgettingAllbookings = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getAllBookings.pending, (state, { payload }) => {
      state.isCoworkSliceFetching = true;
      state.isCoworkSliceSmallFetching = true;
      state.isgettingAllbookings = true;
      return state;
    });
    builder.addCase(getAllRooms.fulfilled, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceSmallFetching = false;
      state.coworkSliceSuccessMessage = payload?.message || " ";
      state.AllRooms = [...state.AllRooms,...payload?.rooms];
      state.AllRoomsCount = payload?.allRoomCount;
      return state;
    });
    builder.addCase(getAllRooms.rejected, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceSmallFetching = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getAllRooms.pending, (state, { payload }) => {
      state.isCoworkSliceFetching = true;
      state.isCoworkSliceSmallFetching = true;
      return state;
    });
    builder.addCase(postUpdateRoom.fulfilled, (state, { payload }) => {
      state.isCoworkSliceSuccess = true;
      state.coworkSliceSuccessMessage = payload?.message || " ";
      return state;
    });
    builder.addCase(postUpdateRoom.rejected, (state, { payload }) => {
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(postUpdateRoom.pending, (state, { payload }) => {
      state.isCoworkSliceFetching = true;
      return state;
    });
    builder.addCase(postRoomActivity.fulfilled, (state, { payload }) => {
      state.isCoworkSliceSmallFetching = false;
      console.log(payload);
      let updatedRoomIndex = state?.AllRooms?.findIndex(
        (data) => data.id === payload?.updatedRoom?.id
      );
      console.log(updatedRoomIndex);
      state.AllRooms[updatedRoomIndex] = payload?.updatedRoom;
      state.isCoworkSliceSuccess = true;
      state.coworkSliceSuccessMessage = payload?.message || " ";
      return state;
    });
    builder.addCase(postRoomActivity.rejected, (state, { payload }) => {
      state.isCoworkSliceSmallFetching = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(postRoomActivity.pending, (state, { payload }) => {
      state.isCoworkSliceSmallFetching = true;
      return state;
    });
    builder.addCase(findSlotsForOneRoom.fulfilled, (state, { payload }) => {
      state.isCoworkSliceSmallFetching = false;
      console.log("findSlotsForOneRoom", payload);
      state.slotsForOneRoom = payload?.roomAndSlots;
      state.availableRoomMyBookings = payload?.myBookings;
      // state.isCoworkSliceSuccess = true;
      state.coworkSliceSuccessMessage = payload?.message || " ";
      return state;
    });
    builder.addCase(findSlotsForOneRoom.rejected, (state, { payload }) => {
      state.isCoworkSliceSmallFetching = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(findSlotsForOneRoom.pending, (state, { payload }) => {
      state.isCoworkSliceSmallFetching = true;
      return state;
    });
    builder.addCase(
      getAdditionalCoworkUsers.fulfilled,
      (state, { payload }) => {
        state.isCoworkSliceSmallFetching = false;
        console.log(payload.users);
        state.additionalUsers = payload.users;
        state.coworkSliceSuccessMessage = payload?.message || " ";
        return state;
      }
    );
    builder.addCase(getAdditionalCoworkUsers.rejected, (state, { payload }) => {
      state.isCoworkSliceSmallFetching = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getAdditionalCoworkUsers.pending, (state, { payload }) => {
      state.isCoworkSliceSmallFetching = true;
      return state;
    });
    builder.addCase(
      addAdditionalCoworkUsers.fulfilled,
      (state, { payload }) => {
        state.isCoworkSliceSmallFetching = false;
        state.attendeesAddedSuccessfully = true;
        state.additionalUsers = payload.users;
        state.coworkSliceSuccessMessage = payload?.message || " ";
        return state;
      }
    );
    builder.addCase(addAdditionalCoworkUsers.rejected, (state, { payload }) => {
      state.isCoworkSliceSmallFetching = false;
      state.attendeesAddedSuccessfully = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(addAdditionalCoworkUsers.pending, (state, { payload }) => {
      state.isCoworkSliceSmallFetching = true;
      return state;
    });
    builder.addCase(postRoomBookingAdmin.fulfilled, (state, { payload }) => {
      state.isCoworkSliceFetchingRoomBooking = false;
      state.isCoworkSliceSuccess = true;
      state.coworkSliceSuccessMessage = "Room Booked Successfully";
      return state;
    });
    builder.addCase(postRoomBookingAdmin.rejected, (state, { payload }) => {
      state.isCoworkSliceFetchingRoomBooking = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(postRoomBookingAdmin.pending, (state, { payload }) => {
      state.isCoworkSliceFetchingRoomBooking = true;
      return state;
    });
    builder.addCase(postResetPasswordEmail.fulfilled, (state, { payload }) => {
      state.isCoworkSliceSuccess = true;
      state.isCoworkSliceSmallFetching = false;
      state.coworkSliceSuccessMessage = payload?.message || " ";
      return state;
    });
    builder.addCase(postResetPasswordEmail.rejected, (state, { payload }) => {
      state.isCoworkSliceSmallFetching = false;
      state.isCoworkSliceError = true;
      console.log(payload)
      state.coworkSliceErrorMessage =
        payload?.Message || "Something Went Wrong";
      return state;
    });
    builder.addCase(postResetPasswordEmail.pending, (state, { payload }) => {
      state.isCoworkSliceSmallFetching = true;
      return state;
    });
    builder.addCase(postTower.fulfilled, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceSuccess = true;
      state.coworkSliceSuccessMessage = payload?.Message || "Tower Added Successfully";
      return state;
    });
    builder.addCase(postTower.rejected, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.Message || "Something Went Wrong";
      return state;
    });
    builder.addCase(postTower.pending, (state, { payload }) => {
      state.isCoworkSliceFetching = true;
      return state;
    });
    builder.addCase(postEquipment.fulfilled, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceSuccess = true;
      state.coworkSliceSuccessMessage = payload?.Message || "Equipment Added Successfully";
      return state;
    });
    builder.addCase(postEquipment.rejected, (state, { payload }) => {
      state.isCoworkSliceFetching = false;
      state.isCoworkSliceError = true;
      state.coworkSliceErrorMessage =
        payload?.Message || "Something Went Wrong";
      return state;
    });
    builder.addCase(postEquipment.pending, (state, { payload }) => {
      state.isCoworkSliceFetching = true;
      return state;
    });

    // ---- Booking color types ----
    builder.addCase(getBookingColorTypes.fulfilled, (state, { payload }) => {
      state.bookingColorTypes = payload?.colorTypes || [];
      return state;
    });
    // Mutations (add / update / delete / assign) share the mutating flag so the
    // dashboard panel can refetch the list once on success.
    const colorTypeMutations = [
      addBookingColorType,
      updateBookingColorType,
      deleteBookingColorType,
      setRequestColorType,
    ];
    colorTypeMutations.forEach((thunk) => {
      builder.addCase(thunk.pending, (state) => {
        state.isColorTypeMutating = true;
        state.colorTypeMutationSuccess = false;
      });
      builder.addCase(thunk.fulfilled, (state) => {
        state.isColorTypeMutating = false;
        state.colorTypeMutationSuccess = true;
      });
      builder.addCase(thunk.rejected, (state, { payload }) => {
        state.isColorTypeMutating = false;
        state.colorTypeMutationSuccess = false;
        state.isCoworkSliceError = true;
        state.coworkSliceErrorMessage = payload?.Message || payload?.message || "Something Went Wrong";
      });
    });

    // A request we aborted ourselves (a duplicate superseded by a newer one —
    // see useLatestRequest) is not a failure. Matchers run after the case
    // reducers, so this undoes the error state the `rejected` case just set and
    // keeps a cancelled duplicate from raising an error toast.
    builder.addMatcher(
      (action) => action.type?.endsWith("/rejected") && action.meta?.aborted,
      (state) => {
        state.isCoworkSliceError = false;
        state.coworkSliceErrorMessage = "";
        state.isgettingTowerListError = false;
        state.isgettingEquipmentListError = false;
      }
    );
  },
});

export const {
  clearCoworkSliceStates,
  clearCoworkSliceData,
  clearCoworkBookingsList,
  clearRoomDetail,
  addRoomDetails,
  addRoomDetailsToBeUpdated,
  addSelectedDate,
  addSelectedSlot,
  clearSelectedSlot,
  addSelectedAttendees,
  clearSelectedAttendees,
  addRoomStatus,
  addAdditionSelectedAttendees,
  clearAdditionalSelectedAttendees,
  addFinalMultipleSlotsForAdmin,
  clearFinalMultipleSlotsForAdmin,
} = coworkSlice.actions;
export const coworkData = (state) => state.coworkData;
export default coworkSlice;
